import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';

const pillars = [
  {
    number: '01',
    title: 'Cognitive Enhancement',
    icon: '/science-page-icons/cognitive.png',
    description:
      "Lion's Mane stimulates Nerve Growth Factor synthesis, driving neuroplasticity and synaptic density. Cordyceps increases ATP production in brain cells — more fuel for faster, clearer thinking.",
    studies: '15+ peer-reviewed studies',
    stat: '87%',
    statLabel: 'Report sharper focus within 30 days',
  },
  {
    number: '02',
    title: 'Cardiovascular Health',
    icon: '/science-page-icons/cardiovascular.png',
    description:
      "Reishi's triterpenes inhibit platelet aggregation and support healthy cholesterol ratios. Long-term use is associated with normalized blood pressure and improved endothelial function.",
    studies: '20+ research trials',
    stat: '40%',
    statLabel: 'Reduction in oxidative stress markers',
  },
  {
    number: '03',
    title: 'Immune Defense',
    icon: '/science-page-icons/immune.png',
    description:
      'Beta-glucans from Shiitake and Turkey Tail bind to macrophage receptors, activating the innate immune cascade without overstimulation. A calibrated response, not an arms race.',
    studies: '30+ research papers',
    stat: '45%',
    statLabel: 'Stronger immune response markers',
  },
  {
    number: '04',
    title: 'Stress Adaptation',
    icon: '/science-page-icons/adaptogen.png',
    description:
      "Ashwagandha and Rhodiola act bidirectionally on the HPA axis — lowering cortisol under chronic stress and maintaining alertness under acute load. Your body's equilibrium, defended.",
    studies: '25+ scientific studies',
    stat: '62%',
    statLabel: 'Reduction in cortisol levels',
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Organic Cultivation',
    body: 'Mushrooms grown on species-appropriate substrates, free from pesticides and GMOs. Harvest timing optimized for peak bioactive density.',
  },
  {
    step: '02',
    title: 'Dual Extraction',
    body: 'Hot-water extraction captures polysaccharides and beta-glucans. Ethanol extraction isolates triterpenes and sterols. Both phases blended for full-spectrum potency.',
  },
  {
    step: '03',
    title: 'Standardization',
    body: 'Each extract standardized to verified concentrations — not just weight. You get the compound, not just the powder.',
  },
  {
    step: '04',
    title: 'Independent Testing',
    body: 'Every batch third-party tested for purity, potency, heavy metals, and microbials before it ships. Certificate of Analysis available on request.',
  },
];

const expertReviews = [
  {
    quote:
      "Lion's Mane is one of the few natural compounds with credible evidence for stimulating Nerve Growth Factor. In my patients, consistent use translates into measurable improvements in focus and mental stamina.",
    name: 'Dr. Sarah Mitchell',
    title: 'MD, Neurologist',
    focus: "On Lion's Mane & cognitive function",
  },
  {
    quote:
      'Reishi has earned its place in integrative practice. Its triterpenes modulate the stress response without sedation — patients report deeper sleep and steadier moods within weeks of daily use.',
    name: 'Dr. James Okafor',
    title: 'MD, Integrative Medicine',
    focus: 'On Reishi & stress adaptation',
  },
  {
    quote:
      'The immunomodulatory effect of beta-glucans from Shiitake and Turkey Tail is well documented. They train the immune system to respond proportionally — which is exactly what you want long-term.',
    name: 'Dr. Elena Vasquez',
    title: 'PhD, Immunology',
    focus: 'On beta-glucans & immune defense',
  },
];

function PillarRow({ pillar, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="border-b"
      style={{ borderColor: 'rgba(26,42,26,0.08)' }}
    >
      <div className={`grid md:grid-cols-2 gap-0`}>
        {/* Icon panel */}
        <div
          className={`flex items-center justify-center py-16 px-12 ${isEven ? 'md:order-1' : 'md:order-2'}`}
          style={{ background: '#F0EDE6' }}
        >
          <img
            src={pillar.icon}
            alt={pillar.title}
            className="w-40 h-40 object-contain"
            style={{ filter: 'drop-shadow(0 4px 16px rgba(184,112,77,0.2))' }}
          />
        </div>

        {/* Text panel */}
        <div
          className={`flex flex-col justify-center py-16 px-12 ${isEven ? 'md:order-2' : 'md:order-1'}`}
          style={{ background: '#FAF9F6' }}
        >
          <div className="flex items-baseline gap-4 mb-6">
            <span className="text-xs font-medium tracking-[0.2em] uppercase" style={{ color: '#B8704D' }}>
              {pillar.number}
            </span>
            <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(26,42,26,0.3)' }}>
              {pillar.studies}
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-light mb-5" style={{ color: '#1A2A1A' }}>
            {pillar.title}
          </h2>

          <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(26,42,26,0.65)', maxWidth: '42ch' }}>
            {pillar.description}
          </p>

          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-light" style={{ color: '#B8704D' }}>{pillar.stat}</span>
            <span className="text-xs" style={{ color: 'rgba(26,42,26,0.45)', maxWidth: '20ch' }}>
              {pillar.statLabel}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function SciencePage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: '-60px' });

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation
        onCartOpen={() => setIsCartOpen(true)}
        cartItemsCount={cartItemsCount}
      />

      {/* Hero */}
      <section style={{ background: '#FAF9F6' }} className="pt-32 pb-24 relative overflow-hidden">
        <div className="container mx-auto px-6 relative">
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 32 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <p className="text-xs font-medium tracking-[0.3em] uppercase mb-8" style={{ color: '#B8704D' }}>
              The Science
            </p>
            <h1 className="text-5xl md:text-7xl font-light leading-tight mb-8" style={{ color: '#1A2A1A' }}>
              Ancient compounds.<br />
              <span style={{ color: 'rgba(26,42,26,0.28)' }}>Modern evidence.</span>
            </h1>
            <p className="text-base leading-relaxed max-w-xl" style={{ color: 'rgba(26,42,26,0.55)' }}>
              Every Berleto formula is built on decades of clinical research and thousands of years of botanical tradition — extracted with precision, validated by independent labs.
            </p>
          </motion.div>

          <div className="mt-20 pt-8 border-t grid grid-cols-3 gap-0" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
            {[
              { n: '3,000+', l: 'Peer-reviewed studies' },
              { n: '100%', l: 'Third-party tested' },
              { n: '0', l: 'Synthetic additives' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={headerInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="px-3 py-4 md:px-8 border-r last:border-r-0"
                style={{ borderColor: 'rgba(26,42,26,0.08)' }}
              >
                <p className="text-xl md:text-3xl font-light mb-1" style={{ color: '#1A2A1A' }}>{s.n}</p>
                <p className="text-[9px] md:text-xs tracking-widest uppercase leading-tight" style={{ color: 'rgba(26,42,26,0.4)' }}>{s.l}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Pillars */}
      <section>
        <div className="border-b" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
          <div className="container mx-auto px-6 py-10">
            <p className="text-xs font-medium tracking-[0.25em] uppercase" style={{ color: '#B8704D' }}>
              Research Areas
            </p>
          </div>
        </div>
        {pillars.map((pillar, i) => (
          <PillarRow key={pillar.number} pillar={pillar} index={i} />
        ))}
      </section>

      {/* Expert Perspectives */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Expert Perspectives
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#1A2A1A' }}>
              What clinicians say.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 border-t" style={{ borderColor: 'rgba(26,42,26,0.1)' }}>
            {expertReviews.map((review, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className={`flex flex-col py-10 md:py-12 md:px-10 border-b md:border-b-0 ${i < 2 ? 'md:border-r' : ''} ${i === 0 ? 'md:pl-0' : ''} ${i === 2 ? 'md:pr-0' : ''}`}
                style={{ borderColor: 'rgba(26,42,26,0.1)' }}
              >
                <p className="text-[10px] tracking-[0.2em] uppercase mb-6" style={{ color: 'rgba(26,42,26,0.35)' }}>
                  {review.focus}
                </p>
                <p className="text-base md:text-lg font-light leading-relaxed mb-8 flex-1" style={{ color: '#1A2A1A' }}>
                  "{review.quote}"
                </p>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#1A2A1A' }}>{review.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#B8704D' }}>{review.title}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24" style={{ background: '#E8DCC4' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              From Soil to Capsule
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#1A2A1A' }}>
              How we make it.
            </h2>
          </motion.div>

          <div className="space-y-px" style={{ background: 'rgba(26,42,26,0.08)' }}>
            {processSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="flex gap-8 items-start px-8 py-8"
                style={{ background: '#E8DCC4' }}
              >
                <span className="text-xs font-medium tracking-widest flex-shrink-0 mt-0.5 w-6" style={{ color: '#B8704D' }}>
                  {step.step}
                </span>
                <div>
                  <h3 className="text-base font-medium mb-2" style={{ color: '#1A2A1A' }}>{step.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.6)', maxWidth: '52ch' }}>{step.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Certifications */}
      <section style={{ background: '#1A2A1A' }} className="py-20" data-bubble-theme="light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Our Commitment
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#FAF9F6' }}>
              No shortcuts. Ever.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px" style={{ background: 'rgba(139,154,139,0.12)' }}>
            {[
              { label: 'USDA Organic', body: 'Certified organic mushrooms, cultivated without pesticides or synthetic fertilizers.' },
              { label: '3rd Party Tested', body: 'Independent lab verification of purity, potency, and safety on every single batch.' },
              { label: 'GMP Certified', body: 'Manufactured in FDA-registered, GMP-certified facilities under strict quality protocols.' },
            ].map((cert, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="px-10 py-12 text-center"
                style={{ background: '#1A2A1A' }}
              >
                <div className="w-px h-10 mx-auto mb-8" style={{ background: '#B8704D' }} />
                <h3 className="text-base font-medium mb-3" style={{ color: '#FAF9F6' }}>{cert.label}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(139,154,139,0.65)' }}>{cert.body}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xs text-center tracking-widest uppercase mt-12"
            style={{ color: 'rgba(139,154,139,0.35)' }}
          >
            Standardized extracts &nbsp;·&nbsp; No fillers &nbsp;·&nbsp; Full-spectrum formulas
          </motion.p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-6" style={{ color: '#B8704D' }}>
              Shop the Range
            </p>
            <h2 className="text-4xl md:text-5xl font-light mb-4" style={{ color: '#1A2A1A' }}>
              The research is clear.
              <br />
              <span style={{ color: 'rgba(26,42,26,0.3)' }}>The choice is yours.</span>
            </h2>
            <p className="text-sm mb-10" style={{ color: 'rgba(26,42,26,0.5)' }}>
              Three formulas. Each built on the same exacting standard.
            </p>
            <button
              onClick={() => navigate('/products')}
              className="inline-block px-10 py-4 text-sm font-medium tracking-wide transition-colors"
              style={{ background: '#1A2A1A', color: '#FAF9F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#243424'}
              onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
            >
              Explore Products
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
